<script>
  import { createNamespacedHelpers } from 'vuex'

  const { mapState, mapActions } = createNamespacedHelpers('pricing')

  export default {
    name: 'CPricing',
    props: {
      activePlanId: {
        type: String,
        default: '',
      },
      includeTrialPlan: {
        type: Boolean,
        default: true,
      },
    },
    data () {
      return {
        isFetching: this.$store.state.pricing.pricingPlans.length === 0,
      }
    },
    computed: {
      ...mapState(['pricingPlans']),
      normalizedPlans () {
        if (!this.pricingPlans) {
          return {}
        }
        return this.pricingPlans
          .filter(({ trial }) => !(!this.includeTrialPlan && trial))
          .map(plan => ({
            ...plan,
            price: plan.price / 100,
            active: plan.id === this.activePlanId,
            planType: this.getPlanType(plan),
            releaseDate: this.getReleaseDateFromBundle(plan.bundle),
            currencySymbol: this.getPlanCurrencySymbol(plan.currencyCode),
          }))
      },
      visitor () {
        return this.$store.state.visitor.visitor
      },
      currentPricingPlan () {
        return this.normalizedPlans.find(({ id }) => id === this.activePlanId) || {}
      },
      activePricingPlans () {
        return this.pricingPlansGrouped.normalPlan[this.visitor.currencyCode] || {}
      },
      multiAnnualPricingPlans () {
        return this.pricingPlansGrouped.multiAnnualPlan?.[this.visitor.currencyCode]?.year || []
      },
      trialPlan () {
        if ('trialPlan' in this.pricingPlansGrouped && this.visitor.currencyCode in this.pricingPlansGrouped.trialPlan) {
          return this.pricingPlansGrouped.trialPlan[this.visitor.currencyCode].day[0]
        }
        return {}
      },
      pricingPlansGrouped () {
        const groups = ['planType', 'currencyCode', 'periodUnit']
        const plansGrouped = { normalPlan: {}, multiAnnualPlan: {} }

        this.newestPricingPlans.forEach((plan) => {
          groups.reduce((obj, group, i) => {
            const key = plan[group]
            obj[key] = obj[key] || (i + 1 === groups.length ? [] : {})
            return obj[plan[group]]
          }, plansGrouped).push(plan)
        })

        return plansGrouped
      },
      newestPricingPlans () {
        const newestPlans = {}
        for (const i in this.normalizedPlans) {
          const plan = this.normalizedPlans[i]

          // if the releaseDate is null it is an old plan => skip
          if (plan.releaseDate === null) {
            continue
          }

          const key = this.getPlanKey(plan.trial, plan.sensors, plan.currencyCode, plan.period, plan.periodUnit, plan.planType)
          if (!(key in newestPlans) || newestPlans[key].releaseDate < plan.releaseDate) {
            newestPlans[key] = plan
          }
        }
        return Object.values(newestPlans)
      },
      plansForActiveSubscription () {
        if (!this.currentPricingPlan) {
          return {}
        }
        return this.pricingPlansGrouped.normalPlan[this.currentPricingPlan.currencyCode] || {}
      },
    },
    async created () {
      await this.$store.dispatch('visitor/fetchVisitor')
      await this.fetchPricingPlans()
      this.isFetching = false
      this.$emit('fetched')
    },
    methods: {
      ...mapActions(['fetchPricingPlans']),
      getReleaseDateFromBundle (bundle) {
        const splitted = bundle.split('-')

        // when bundle is not well formated => it's an old plan => skip
        if (splitted.length !== 4) {
          return null
        }
        return `${splitted[3]}${splitted[2]}${splitted[1]}`
      },
      getPlanKey (isTrial, sensors, currencyCode, period, periodUnit, planType) {
        let trialCode = 'normalPlan'
        if (isTrial) {
          trialCode = 'trialPlan'
        }
        if (planType === 'multiAnnualPlan') {
          trialCode = 'multiAnnualPlan'
        }
        return `${trialCode}_${sensors}_${currencyCode}_${period}_${periodUnit}`
      },
      getPlanType (plan) {
        if (plan.trial) {
          return 'trialPlan'
        }
        if (plan.period && parseInt(plan.period) > 1 && plan.periodUnit === 'year') {
          return 'multiAnnualPlan'
        }
        return 'normalPlan'
      },
      hasMultiplePeriodUnitPlans (plans) {
        if (this.currentPricingPlan && this.currentPricingPlan.periodUnit === 'year') {
          return false
        }

        if (plans) {
          return ('month' in plans && 'year' in plans)
        }
        return false
      },
      isNewestPricePlan (planId) {
        for (const i in this.newestPricingPlans) {
          if (this.newestPricingPlans[i].id === planId) {
            return true
          }
        }

        return false
      },
      getPlanCurrencySymbol (currencyCode) {
        if (currencyCode === 'EUR') {
          return '€'
        }
        return '$'
      },
    },
    render () {
      return this.$scopedSlots.default({
        pricingPlans: this.normalizedPlans,
        currentPricingPlan: this.currentPricingPlan,
        isFetching: this.isFetching,
        trialPlan: this.trialPlan,
        activePricingPlans: this.activePricingPlans,
        multiAnnualPricingPlans: this.multiAnnualPricingPlans,
        isNewestPricePlan: this.isNewestPricePlan,
        hasMultiplePeriodUnitPlans: this.hasMultiplePeriodUnitPlans,
        plansForActiveSubscription: this.plansForActiveSubscription,
      })
    },
  }
</script>
